import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SafewardClient, SafewardHttpError } from '../safeward-client.js'
import type { AppSettings, ZerionPositionsPage } from '../types.js'

export type PositionsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; page: ZerionPositionsPage; refreshedAt: Date }
  | { status: 'error'; message: string; isUnavailable: boolean }

export interface UsePositions {
  state: PositionsState
  refresh: (force?: boolean) => Promise<void>
}

const HOUR_MS = 60 * 60 * 1000

/**
 * Per-token holdings. Lazy by design — pass `enabled: true` only when the
 * UI surfaces actually need the (potentially huge) list. The 60s in-flight
 * cache in SafewardClient + 1h worker KV cache means re-enables within an
 * hour cost nothing.
 */
export function usePositions(
  settings: AppSettings,
  address: string | null,
  enabled: boolean,
): UsePositions {
  const [state, setState] = useState<PositionsState>({ status: 'idle' })

  const client = useMemo(
    () =>
      new SafewardClient({
        baseUrl: settings.safewardUrl,
        authToken: settings.safewardAuthToken,
      }),
    [settings.safewardUrl, settings.safewardAuthToken],
  )
  const clientRef = useRef(client)
  clientRef.current = client

  const addressRef = useRef(address)
  addressRef.current = address

  const refresh = useCallback(async (force = false) => {
    const addr = addressRef.current
    if (!addr) {
      setState({ status: 'idle' })
      return
    }
    setState((prev) =>
      prev.status === 'ready' ? prev : { status: 'loading' },
    )
    try {
      const page = await clientRef.current.positions(addr, {
        refresh: force,
        includeYields: true,
      })
      setState({ status: 'ready', page, refreshedAt: new Date() })
    } catch (err) {
      const isUnavailable =
        err instanceof SafewardHttpError && err.status === 503
      setState({
        status: 'error',
        message: (err as Error).message,
        isUnavailable,
      })
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void refresh(false)
  }, [refresh, address, enabled])

  // Roll the snapshot forward into the next hourly KV window when the
  // expander stays open across the boundary.
  useEffect(() => {
    if (!enabled || !address) return
    const t = setInterval(() => void refresh(false), HOUR_MS)
    return () => clearInterval(t)
  }, [refresh, address, enabled])

  return { state, refresh }
}
